import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CreateChatViewEnhanced from '../views/CreateChatView';
import { createNewDM, convertChatThreadToRoomInfo } from '@edusense/people-chat/handlers/DM';
import { useDMListStore, useMessageStore } from '../../stores';
import { chatApiClient } from '@edusense/people-chat/api/chatApiClient';
// chatApiClient에 토큰 설정
const initializeChatApiClient = ()=>{
    const token = localStorage.getItem('auth_token');
    if (token) {
        chatApiClient.setToken(token);
    }
};
const CreateChatViewContainer = ({ onBack, onSwitchToAIChat, onChatCreated, currentUserId = '1' // 임시 기본값
 })=>{
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const queryClient = useQueryClient();
    const { setDMLIst } = useDMListStore();
    const { setUnreadCount } = useMessageStore();
    // 새 채팅방 생성 뮤테이션
    const createChatMutation = useMutation({
        mutationFn: async ({ recipients, groupName, initialMessage })=>{
            const participantUserIds = recipients.map((user)=>user.userId.toString());
            const isGroupThread = recipients.length > 1;
            return await createNewDM(participantUserIds, isGroupThread, initialMessage || `안녕하세요! ${isGroupThread ? '그룹 채팅' : '대화'}을 시작했습니다.`);
        },
        onSuccess: (chatThread, variables)=>{
            console.log('Chat created successfully:', chatThread);
            // 로컬 스토어 업데이트
            const roomInfo = convertChatThreadToRoomInfo(chatThread, currentUserId);
            setDMLIst((prev)=>({
                    ...prev,
                    [chatThread.chatRoomId]: roomInfo
                }));
            // 쿼리 캐시 무효화
            queryClient.invalidateQueries({
                queryKey: [
                    'chatThreads'
                ]
            });
            // 콜백 호출 - 채팅창으로 자동 이동 처리
            if (onChatCreated) {
                onChatCreated(chatThread);
            // onChatCreated에서 채팅창을 열므로 onBack() 호출하지 않음
            } else {
                // 콜백이 없는 경우에만 뒤로가기
                onBack();
            }
        },
        onError: (error)=>{
            console.error('Failed to create chat:', error);
            setCreateError(error instanceof Error ? error.message : '채팅방 생성에 실패했습니다.');
        }
    });
    // 컴포넌트 마운트 시 토큰 초기화
    useEffect(()=>{
        initializeChatApiClient();
    }, []);
    // 채팅방 생성 핸들러
    const handleCreateChat = useCallback(async (recipients, groupName)=>{
        if (recipients.length === 0) {
            setCreateError('최소 한 명의 참여자를 선택해주세요.');
            return;
        }
        setIsCreating(true);
        setCreateError(null);
        try {
            await createChatMutation.mutateAsync({
                recipients,
                groupName,
                initialMessage: groupName ? `"${groupName}" 그룹 채팅이 시작되었습니다.` : undefined
            });
        } catch (error) {
        // 에러는 mutation의 onError에서 처리됨
        } finally{
            setIsCreating(false);
        }
    }, [
        createChatMutation
    ]);
    // AI 채팅 전환 핸들러 (기존 기능 유지)
    const handleSwitchToAIChat = useCallback(()=>{
        if (onSwitchToAIChat) {
            onSwitchToAIChat();
        }
    }, [
        onSwitchToAIChat
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            position: 'relative',
            height: '100%'
        },
        children: [
            /*#__PURE__*/ _jsx(CreateChatViewEnhanced, {
                onBack: onBack,
                onSwitchToAIChat: handleSwitchToAIChat,
                onCreateChat: handleCreateChat,
                isCreating: isCreating
            }),
            isCreating && /*#__PURE__*/ _jsx("div", {
                style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    style: {
                        backgroundColor: 'white',
                        padding: '2rem',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '1rem'
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                width: '2rem',
                                height: '2rem',
                                border: '3px solid #e5e7eb',
                                borderTop: '3px solid #3b82f6',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                color: '#6b7280',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                            },
                            children: "채팅방을 생성하고 있습니다..."
                        })
                    ]
                })
            }),
            createError && /*#__PURE__*/ _jsxs("div", {
                style: {
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    right: '1rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    color: '#dc2626',
                    fontSize: '0.875rem',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                },
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: createError
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>setCreateError(null),
                        style: {
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            fontSize: '1rem'
                        },
                        children: "×"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("style", {
                children: `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `
            })
        ]
    });
};
export default CreateChatViewContainer;
